import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import FormActionButtons from '../Assets/FormActionButtons';
import LanguageTabs, { FormLang } from '../Assets/LanguageTabs';
import { useTranslation } from 'react-i18next';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (entry: {
    text: { en: string; mk: string; sq?: string };
  }) => void;
  initialData?: {
    text: { en: string; mk: string; sq?: string };
  };
}

type Multi = { en: string; mk: string; sq: string };

const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<FormLang>('en');
  const [text, setText] = useState<Multi>({ en: '', mk: '', sq: '' });

  useEffect(() => {
    if (initialData) {
      setText({
        en: initialData.text.en || '',
        mk: initialData.text.mk || '',
        sq: initialData.text.sq || '',
      });
    } else {
      resetForm();
    }
  }, [initialData, isOpen]);

  const stripHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent?.trim() || '';
  };

  const isSubmitDisabled = !stripHtml(text.en) || !stripHtml(text.mk);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ text });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setText({ en: '', mk: '', sq: '' });
    setActiveLang('en');
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link',
  ];

  if (!isOpen) return null;

  const langSuffix = activeLang.toUpperCase();

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-semibold mb-6 text-blue-900 text-center">
          {t(`forms.createHistory`)}
        </h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium text-gray-700 mb-1">{t('forms.text')} ({langSuffix})</label>
            <ReactQuill
              key={`text-${activeLang}`}
              value={text[activeLang]}
              onChange={(value) => setText((prev) => ({ ...prev, [activeLang]: value }))}
              theme="snow"
              className="h-48 mb-8"
              modules={quillModules}
              formats={quillFormats}
            />
          </div>
          <br />
          <br />
          <FormActionButtons
            edit={false}
            onClose={handleCancel}
            onSubmit={handleSubmit}
            disabled={isSubmitDisabled}
          />
        </form>
      </div>
    </div>
  );
};

export default HistoryModal;
